import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    Input,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DesktopDownloadService } from 'app/core/desktop/desktop-download.service';
import { PwaInstallService } from 'app/core/pwa/pwa-install.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-install-button',
    templateUrl: './app-install-button.component.html',
    styleUrls: ['./app-install-button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class AppInstallButtonComponent {
    @Input() variant: 'icon' | 'button' | 'footer' | 'chip' | 'menu' = 'icon';

    private readonly _pwa = inject(PwaInstallService);
    private readonly _desktop = inject(DesktopDownloadService);
    private readonly _toast = inject(ToastrService);
    private readonly _cdr = inject(ChangeDetectorRef);

    showInstall = false;
    tooltip = 'Install BizCare on your desktop';

    constructor() {
        effect(() => {
            this._pwa.showInstallUi();
            this._pwa.canInstall();
            this._pwa.isInstalled();
            this.showInstall = this._pwa.showInstallUi();
            this.tooltip = this._desktopTooltip();
            this._cdr.markForCheck();
        });
    }

    async install(): Promise<void> {
        // Desktop browsers → Electron installer download (primary).
        if (this._desktop.isDesktopBrowser() && this._desktop.isConfigured()) {
            const result = this._desktop.download();
            const os = this._desktop.osLabel();

            if (result === 'started') {
                this._toast.success(
                    `Downloading BizCare for ${os}. Open the file when it finishes to install.`,
                    'Desktop app',
                    { timeOut: 8000, closeButton: true }
                );
                return;
            }

            if (result === 'unavailable') {
                this._toast.warning(
                    `Desktop installer for ${os} is not available yet. Try again later or use the browser install option.`,
                    'Desktop app',
                    { timeOut: 8000, closeButton: true }
                );
                // Fall through to PWA if the browser supports it.
            }
        }

        const result = await this._pwa.install();

        if (result === 'reloading') {
            return;
        }

        if (result === 'ios') {
            this._toast.info(this._pwa.iosInstallHint(), 'Install on iPhone/iPad', {
                timeOut: 8000,
                closeButton: true,
            });
            return;
        }

        if (result === 'unavailable') {
            this._toast.info(this._fallbackHint(), 'Install BizCare', {
                timeOut: 8000,
                closeButton: true,
            });
            return;
        }

        if (result === 'accepted') {
            this._toast.success(
                'BizCare installed. Check your desktop for the shortcut.',
                'Installed'
            );
        }
    }

    private _desktopTooltip(): string {
        if (this._desktop.isDesktopBrowser() && this._desktop.isConfigured()) {
            return `Download BizCare desktop app for ${this._desktop.osLabel()}`;
        }

        if (this._pwa.canInstall()) {
            return 'Install BizCare on your desktop';
        }

        if (this._pwa.isIosInstallable()) {
            return this._pwa.iosInstallHint();
        }

        return this._pwa.desktopInstallHint();
    }

    private _fallbackHint(): string {
        if (this._desktop.isDesktopBrowser()) {
            return `Download is temporarily unavailable. You can also use the browser menu (⋮) → Install BizCare.`;
        }

        return this._pwa.desktopInstallHint();
    }
}
